'use client'
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Wrapper,
  Container,
  P2,
  PrimaryFont,
  RecklessFont,
} from "@components/styled";
import { Link as PrismicLink } from '@components/CustomLink';
import styled, { css } from 'styled-components';
import theme, { media } from "@styles/theme";
import { throttle } from '@helpers/throttle';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby'
import { Link as CustomLink } from '@components/CustomLink';
import LogoIcon from '@assets/NewLightLogoIcon.svg';

/* ============================================================================
 * Types & normalization
 * ========================================================================== */

type NavNode = {
  id: string
  label: string
  link?: any
  children?: NavNode[]
}

const GRANDCHILD_SLOT_COUNT = 10

function normalizeLevel3(item: any, parentId: string): NavNode[] | undefined {
  const nodes: NavNode[] = []
  for (let i = 1; i <= GRANDCHILD_SLOT_COUNT; i++) {
    const name = item[`grandchild_${i}_name`]
    const link = item[`grandchild_${i}_link`]
    if (name || (link && link.url)) {
      nodes.push({ id: `${parentId}-gc${i}`, label: name ?? '', link })
    }
  }
  return nodes.length ? nodes : undefined
}

function normalizeNavigationData(slices: any[]): NavNode[] {
  if (!Array.isArray(slices)) return []
  return slices.map((slice: any, idx: number): NavNode => {
    const parentId = `nav-${idx}`
    const level2: NavNode[] = Array.isArray(slice.items)
      ? slice.items.map((item: any, childIdx: number): NavNode => {
          const childId = `${parentId}-${childIdx}`
          return {
            id: childId,
            label: item.child_name ?? '',
            link: item.child_link,
            children: normalizeLevel3(item, childId),
          }
        })
      : []
    return {
      id: parentId,
      label: slice.primary?.name ?? '',
      link: slice.primary?.logo_link,
      children: level2,
    }
  })
}

function getLinkUrl(link: any): string | undefined {
  if (!link) return undefined
  return link.url
}

function getPathname(url: string | undefined): string | undefined {
  if (!url) return undefined
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

function isDescendantActive(node: NavNode, pathname: string | undefined): boolean {
  if (!pathname) return false
  if (getPathname(getLinkUrl(node.link)) === pathname) return true
  if (!node.children) return false
  return node.children.some((child) => isDescendantActive(child, pathname))
}

/* ============================================================================
 * Styled components
 * ========================================================================== */

const StyledContainer = styled(Container as any)`
  @media (min-width: 1192px) {
    max-width: 1192px;
    padding-right: 36px;
    padding-left: 36px;
  }
`;

const Angle = styled.div`
  position: relative;
  margin-left: auto;
`;

export const HeaderButton = styled.button`
  display: flex;
  width: 100%;
  border: none;
  outline: none;
  padding: 0;
  text-align: left;
  background: none;
  position: relative;
  padding-right: 20px;
  ${RecklessFont};

  ${P2} {
    margin: 0;
    font-size: 24px;
    font-weight: 400;
    font-family: inherit;
  }
`;

export const CollapseBody = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  padding: 0;
  margin: 0;
  height: 1px;
  pointer-events: none;
  overflow: hidden;
  opacity: 0;
  grid-gap: 4px;

  li {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
`;

const MobileItemLinkWrapper = styled.span`
  a {
    font-size: 16px;
  }
`;

const MobileLinkWrapper = styled.div`
  a {
    ${RecklessFont};
    font-size: 24px;
    font-weight: normal;
  }
`;

const MobileL2Button = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  border: none;
  outline: none;
  padding: 0;
  text-align: left;
  background: none;
  color: inherit;
  font-size: 16px;
  line-height: 140%;
  font-weight: 400;
  padding: 4px 20px 4px 0;
  position: relative;

  ${Angle} {
    margin-left: auto;
    transition: transform 0.3s;
  }
`;

const MobileLevel3List = styled.ul`
  list-style-type: none;
  padding: 8px 0 8px 20px;
  margin: 8px 0 4px 0;
  border-left: 1px solid rgba(${theme.colors.darkGreyRGB}, 0.25);
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    font-size: 14px;
    line-height: 140%;
    opacity: 0.9;
  }

  a {
    text-decoration: none;
    color: inherit;
  }
`;

const MobileL2Wrapper = styled.li<{ $expanded: boolean }>`
  list-style-type: none;

  ${({ $expanded }) =>
    $expanded
      ? css`
          ${MobileL2Button} ${Angle} {
            transform: rotate(180deg);
          }
          ${MobileLevel3List} {
            display: flex;
          }
        `
      : css`
          ${MobileLevel3List} {
            display: none;
          }
        `}
`;

export const Collapse = styled.li<{ $expanded: boolean, lightIndicator?: boolean }>`
  padding: 24px 0;
  border-top: 1px solid rgba(${theme.colors.darkGreyRGB}, 0.2);

  ${({ $expanded, lightIndicator }) => $expanded ? css`
    ${HeaderButton} {

      ${Angle} {
        transform: rotate(180deg);
      }
    }
  
    ${CollapseBody} {
      height: auto;
      pointer-events: initial;
      overflow: visible;
      opacity: 1;
      padding-top: 20px;
    }
  ` : css`
    ${HeaderButton} {
      &:after {
        ${lightIndicator ? css`

          background: brown;
        ` : css`

          background: red;
        `}
      }
    }
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  height: 30px;
  width: 30px;
  background: transparent;
  transform: rotate(45deg);
  color: #B0D3DD;

  &:after {
    content: "";
    height: 30px;
    width: 3px;
    background: currentColor;
    position: absolute;
  }

  &:before {
    content: "";
    height: 3px;
    width: 30px;
    background: currentColor;
    position: absolute;
  }
`;

const CloseButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 4px;
  padding-bottom: 82px;
  position: relative;
`;

const BurgerButton = styled.button`
  height: 26px;
  width: 46px;
  position: relative;
  border: none;
  cursor: pointer;
  background: none;

  @media (min-width: 1201px) {
    display: none;
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    height: 3px;
    width: 30px;
    top: 6px;
    left: 8px;
    background-color: ${({ theme }) => theme.colors.dark};
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    height: 3px;
    width: 30px;
    bottom: 6px;
    left: 8px;
    background-color: ${({ theme }) => theme.colors.dark};
  }
`;


const StyledWrapper = styled(Wrapper) <{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  transition: all .3s;
  top: 0;
  width: 100%;
  z-index: 30;

  p {
    ${PrimaryFont};
  }

  ${({ $scrolled }) => $scrolled && css`
    color: ${({ theme }) => theme.colors.dark};
  `}

  img.logo {
    margin-left: 32px;
    transform: scale(1.3);
    transition: .3s;

    @media ${media.sm} {
      margin-left: 0;
      transform: scale(1);
    }
  }

  ${({ $scrolled }) => $scrolled && css`
    background: #fff;
    padding: 3px 0;

    img.logo {
      transform: scale(1);
      margin-left: 0;
    }
  `}
`;

const DesktopMenu = styled.ul`
  display: flex;
  align-items: center;

  & > li {
    margin-right: 23px;
  }

  a {
    font-size: 16px;
  }
`


const LogIn = styled.ul<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  margin: 0;
  white-space: nowrap;
  position: relative;

  a {
    padding: 12px 28px;
    display: inline-block;
    font-size: 18px;
    border-radius: 99999px;


    margin-right: 8px;
    display: inline-block;
    margin: 10px 0;
    ${({ $isScrolled, theme }) => $isScrolled ? css`
      color: ${theme.colors.dark};
    ` : css`
      background: #F5F5AB;
      color: #58583E;
    `}
    transition: all .3s;

    @media ${media.md_up} {
      &:hover {
        background-color: #fff;
      }
    }
  }

  @media (max-width: 1200px) {
    display: none !important;
  }

  li {
    color: inherit;
  }

  li:not(:last-child) {
    &:after {
      content: '';
      height: 4px;
      width: 4px;
      border-radius: 50%;
      position: absolute;
      top: calc(50% - 2px);
    }
  }
`

const LogoWrapper = styled.div`
  width: 50%;
  height: 48px;

  @media ${media.sm_up} {
    width: 25%;
  }

  a {
    height: 100%;
    display: flex;
  }

  img {
    max-width: 194px;

    @media ${media.sm} {
      max-width: 158px;
    }
  }

  &.mobile {
    height: auto;
    width: 158px;
  }

`;

const HeaderComponent = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${DesktopMenu}, ${LogIn} {
    display: flex;
    list-style-type: none;
    align-items: baseline;

    &:first-child {
      margin: 0;
      padding: 0;
    }

    & > li {
      position: relative;

      & > a {
        &:not(.underlined) {
          text-decoration: none;
        }
        text-decoration: none;
        p {
          margin: 0;
          text-decoration: none;
        }
      }

    }
  }

  ${DesktopMenu} {
    @media (max-width: 1200px) {
      display: none;
    }
  }

  ${LogIn} {
    & > li {
    }
  }
`

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 50%;

  @media ${media.xl} {
    width: 50%;
  }

  @media (max-width: 1200px) {
    display: none;
  }

  @media ${media.sm} {
    display: none;
  }

  @media ${media.s} {
    width: auto;
  }
`

const DropdownInnerContent = styled.ul`
  list-style-type: none;
`;


const LinkWrapper = styled.li<{ $active: boolean }>`
  ${({ $active }) => $active ? css`
    a {
      text-decoration: underline !important;
      color: #29525E !important;
    }

    p {
      color: #29525E;
    }
  ` : css`
    p {
      color: #29525E;
    }
  `}

  a {
    transition: text-decoration .3s, opacity .3s;

    &:hover {
      text-decoration: underline !important;
      opacity: .6;
      color: #29525E !important;
    }
  }
`

const DropdownContent = styled.div`
  position: absolute;
  overflow: visible;
  min-width: 260px;
  background-color: #fff;
  margin-left: -25px;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.15);

  @media (max-width: 1440px) {
    max-width: 210px;
  }

  ${LinkWrapper} {
    &:first-child {
      p {
        padding-top: 25px;
      }
    }

    &:last-child {
      p {
        padding-bottom: 25px;
      }
    }

    &:not(:first-child) {
      border-top: 1px solid #29525e66;
    }

    a {
      display: block;
      text-decoration: none;
    }

    p {
      margin: 0;
      padding: 15px 0;
    }
  }
`;


const DropdownHead = styled.button<{ $withDropdown: boolean }>`
  position: relative;
  z-index: 1;
  background: none;
  border: none;
  transition: opacity .3s;
  white-space: nowrap;
  p {
    font-size: 16px;
  }

  p {
    font-size: 16px;
  }

  ${({ $withDropdown }) => $withDropdown ? css`
    padding: 12px 17px 12px 0;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.dark};
    
    &:after {
      content: '';
      position: absolute;
      right: 0;
      top: calc(50% - 2px);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;

      border-top: 6px solid currentColor;
    }

    &.active {
      border-bottom: 2px solid #FA5C40;
    }
  ` : css`
    padding: 0;
  `}

  p {
    margin: 0;
  }
`;


const CustomLinkWrapper = styled.span<{ $showArrow: boolean }>`
  position: relative;
  padding: 14px 0 14px 0;
  display: block;
  transition: opacity .3s;
  
  &:hover {
    opacity: .6;

    a.link {
      text-decoration: underline !important;
    }
  }

  ${({ $showArrow }) => $showArrow && css`
    &:after {
      content: '';
      position: absolute;
      right: 0;
      top: calc(50% - 2px);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;

      border-top: 6px solid currentColor;
    }
  `}

  a {
    color: currentColor;
    text-decoration: none;

    &:hover {
      opacity: .6;
      color: #29525E !important;
    }
  }

  &.active {
    border-bottom: 2px solid #FA5C40;
  }
`;

const DropdownBody = styled.div<{ $expanded: boolean }>`

  ${DropdownInnerContent} {
    padding: 0 25px;
  }

  ${({ $expanded }) => $expanded && css`

    ${DropdownHead}, ${CustomLinkWrapper} {
      &:after {
        border-top: transparent;
        border-bottom: 6px solid currentColor;
      }
    }
  `}
`;

/* --- Level 3 desktop flyout --- */

const Flyout = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  min-width: 240px;
  background-color: #fff;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.15);
  padding: 0 25px;

  @media (max-width: 1440px) {
    min-width: 210px;
  }
`;

const FlyoutList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const FlyoutItem = styled.li<{ $active: boolean }>`
  &:first-child p {
    padding-top: 15px;
  }
  &:last-child p {
    padding-bottom: 15px;
  }
  &:not(:first-child) {
    border-top: 1px solid #29525e33;
  }

  a {
    display: block;
    text-decoration: none;
    transition: opacity 0.3s;
    color: #29525e;

    &:hover {
      opacity: 0.6;
      text-decoration: underline !important;
    }
  }

  p {
    margin: 0;
    padding: 12px 0;
    color: #29525e;
    font-size: 14px;
  }

  ${({ $active }) =>
    $active &&
    css`
      a {
        text-decoration: underline !important;
      }
      p {
        font-weight: 500;
      }
    `}
`;

const L2Item = styled.li<{ $active: boolean; $hasChildren: boolean }>`
  position: relative;
  list-style-type: none;

  & > a,
  & > button {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 15px 0;
    text-decoration: none;
    transition: opacity 0.3s;
    color: #29525e;
    font-size: 16px;

    &:hover {
      opacity: 0.6;
    }
  }

  ${({ $hasChildren }) =>
    $hasChildren &&
    css`
      & > a:after,
      & > button:after {
        content: '';
        margin-left: auto;
        flex-shrink: 0;
        width: 0;
        height: 0;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-left: 7px solid currentColor;
        opacity: 0.88;
      }
    `}

  ${({ $active }) =>
    $active &&
    css`
      & > a,
      & > button {
        text-decoration: underline;
        font-weight: 500;
      }
    `}

  &:not(:first-child) {
    border-top: 1px solid #29525e33;
  }
`;

const MobileSocialWrapper = styled.div`
  display: flex;
  grid-gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
  margin-bottom: 16px;

  a {
    display: inline-block;
  }
`;

const MobileMenuButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;

  a {
    text-decoration: none;
    text-align: center;
    padding: 15px 28px;
    display: inline-block;
    font-size: 18px;
    border-radius: 99999px;

    margin-right: 8px;
    display: inline-block;
    margin: 10px 0;
    background: #F5F5AB;
    color: #58583E;
    transition: all .3s;

    @media ${media.md_up} {
      &:hover {
        background-color: #fff;
      }
    }
  }
`;

const MobileMenuWrapper = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin: 0;
  padding: 0;

  * {
    color: #B0D3DD;
  }

  a {
    color: #B0D3DD;
    text-decoration: none;
  }
`;

const MobileInner = styled.div`
  flex-direction: column;
  padding: 16px;
  color: #B0D3DD;
`;

const MobileOverlay = styled.div<{ $showMobileMenu: string }>`
  height: 100vh;
  width: 100vw;
  background: ${theme.colors.dark};
  position: fixed;
  top: 0;
  left: 0;
  flex-direction: column;
  overflow: auto;
  display: ${({ $showMobileMenu }) => $showMobileMenu ? "flex" : "none"};

  ${({ $showMobileMenu }) => $showMobileMenu ? css`
    ${MobileInner} {
      display: flex;
    }
  ` : css`
    display: none;
  `}

  & > div > ul {
    & > li:last-of-type {
      border-bottom: 1px solid rgba(${theme.colors.darkGreyRGB}, 0.2);
    }
  }
`;

const StyledP2 = styled(P2)`
  @media ${media.lg_up} {
    font-size: 16px;
  }
`;

/* ============================================================================
 * Helpers
 * ========================================================================== */

export const getButtonColorAndBGFromString = (colorString: any) => {
  if (!colorString) return;

  const lowercasedString = colorString.toLowerCase();

  switch (lowercasedString) {
    case 'brand green':
      return 'bg-brand-green text-brand-light-green';
    case 'brand light green':
      return 'bg-brand-light-green text-brand-green transition-colors duration-300 md:hover:bg-brand-green md:hover:text-brand-light-green';
    case 'brand yellow':
      return 'bg-brand-yellow text-brand-grey transition-colors duration-300 md:hover:bg-[#fff] md:hover:text-brand-grey';
    case 'brand grey':
      return 'bg-brand-grey text-brand-yellow';
    case 'brand light':
      return 'bg-brand-light text-brand-green transition-colors duration-300 md:hover:bg-brand-light-green md:hover:text-brand-green border border-[#29525e33]';
    case 'transparent':
      return 'border border-[#b0d3dd33] rounded-full text-brand-light-green'
    case 'brand light green with dark text':
      return 'border border-[#29525e33] rounded-full text-brand-green';
    case 'transparent yellow':
      return 'text-brand-yellow transition-colors duration-300 md:hover:text-brand-yellow border border-[#f5f5ab7f] md:hover:border-[#f5f5ab]'
    default: return colorString;
  }
};


function getColor(colorString: string | undefined) {
  if (!colorString) return "";

  switch (colorString?.toLowerCase()) {
    case 'brand light green':
      return 'text-brand-light-green';
    case 'brand yellow':
      return 'text-brand-yellow'
    default: return "";
  }
}

const getTextColor = (colorString: string | undefined) => {
  if (!colorString) return 'text-brand-green';

  switch (colorString.toLowerCase()) {
    case 'brand green':
      return 'text-brand-light-green';
    case 'brand grey':
      return 'text-brand-yellow';
    default: return 'text-brand-green';
  }
}

/* ============================================================================
 * Navigation data (hardcoded — matches navigation_item_three_level slices)
 * ========================================================================== */

const globalParams = {
  data: {
    twitter_icon_link: {
      "link_type": "Web",
      "url": "https://twitter.com/CounterpartIns",
      "target": "_blank"
    },
    linkedin_icon_link: {
      "link_type": "Web",
      "url": "https://www.linkedin.com/company/yourcounterpart/",
      "target": "_blank"
    },
    slices: [
      {
        "variation": "default",
        "version": "initial",
        "items": [
          {
            "child_name": "Management Liability",
            "child_link": {
              "id": "adcFexoAAEEArR-C",
              "type": "service",
              "tags": [],
              "lang": "en-us",
              "slug": "management-liability",
              "first_publication_date": "2026-04-24T19:48:00+0000",
              "last_publication_date": "2026-04-28T23:22:31+0000",
              "uid": "management-liability",
              "url": "https://yourcounterpart.com/services/management-liability",
              "link_type": "Web",
              "key": "203d736c-23e7-4836-8ffb-e225202d023f",
              "isBroken": false
            },
            "grandchild_1_name": "Private Directors & Officers",
            "grandchild_1_link": {
              "id": "ZTgMhRIAAImb2TH-",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "directors--officers-liability-insurance",
              "first_publication_date": "2023-10-24T18:31:19+0000",
              "last_publication_date": "2026-04-24T20:00:59+0000",
              "uid": "do",
              "url": "https://yourcounterpart.com/products/do",
              "link_type": "Web",
              "key": "2aace2e7-abe1-4099-8f5a-47ce4fd38e18",
              "isBroken": false
            },
            "grandchild_2_name": "Not-For-Profit Directors & Officers",
            "grandchild_2_link": {
              "id": "Zl_DcBMAACwAi8Mq",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "not-for-profit-directors--officers-liability-insurance",
              "first_publication_date": "2024-06-11T23:49:50+0000",
              "last_publication_date": "2025-11-20T15:13:19+0000",
              "uid": "not-for-profit-do",
              "url": "https://yourcounterpart.com/products/not-for-profit-do",
              "link_type": "Web",
              "key": "7a5cb7af-4ab6-4850-ad99-e0ffe5698b9f",
              "isBroken": false
            },
            "grandchild_3_name": "Employment Practices",
            "grandchild_3_link": {
              "id": "ZTflQhIAAHeI2HyS",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "employment-practices-liability-insurance",
              "first_publication_date": "2023-10-24T18:31:34+0000",
              "last_publication_date": "2025-11-20T15:10:57+0000",
              "uid": "epli",
              "url": "https://yourcounterpart.com/products/epli",
              "link_type": "Web",
              "key": "04b08e7b-2e04-41df-97ae-c43d8238114a",
              "isBroken": false
            },
            "grandchild_4_name": "Fiduciary",
            "grandchild_4_link": {
              "id": "ZTgMTxIAAImb2TEL",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "fiduciary-liability-insurance",
              "first_publication_date": "2023-10-24T18:31:27+0000",
              "last_publication_date": "2025-11-20T15:11:27+0000",
              "uid": "fid",
              "url": "https://yourcounterpart.com/products/fid",
              "link_type": "Web",
              "key": "30ae06ad-850d-4557-a9ac-ab4c8318ac65",
              "isBroken": false
            },
            "grandchild_5_name": "Commercial Crime",
            "grandchild_5_link": {
              "id": "ZTgLpBIAACOb2S3h",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "commercial-crime-liability-insurance",
              "first_publication_date": "2023-10-25T22:23:30+0000",
              "last_publication_date": "2025-11-20T16:36:29+0000",
              "uid": "crime",
              "url": "https://yourcounterpart.com/products/crime",
              "link_type": "Web",
              "key": "6a293a1d-a5e1-4939-b398-63db2c225793",
              "isBroken": false
            },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Professional Liability",
            "child_link": {
              "id": "adbyrBoAAEYArQQ1",
              "type": "service",
              "tags": [],
              "lang": "en-us",
              "slug": "professional-liability",
              "first_publication_date": "2026-04-24T19:50:24+0000",
              "last_publication_date": "2026-04-28T23:22:54+0000",
              "uid": "professional-liability",
              "url": "https://yourcounterpart.com/services/professional-liability",
              "link_type": "Web",
              "key": "eb07c55b-0113-4b9e-ba94-5ea897f21ea1",
              "isBroken": false
            },
            "grandchild_1_name": "Miscellaneous Professional Liability",
            "grandchild_1_link": {
              "id": "ZLBd9xIAACEAa890",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "counterpart-miscellaneous-professional-liability",
              "first_publication_date": "2023-07-13T20:26:36+0000",
              "last_publication_date": "2025-11-20T15:15:40+0000",
              "uid": "miscellaneous-professional",
              "url": "https://yourcounterpart.com/products/miscellaneous-professional",
              "link_type": "Web",
              "key": "c6a1de60-8f5f-4df2-adcf-aba4ca6a5983",
              "isBroken": false
            },
            "grandchild_2_name": "Allied Healthcare & Medical",
            "grandchild_2_link": {
              "id": "aBQpJBkAAE0AEVeP",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "allied-medical--healthcare-liability",
              "first_publication_date": "2025-07-10T15:00:10+0000",
              "last_publication_date": "2025-08-15T18:20:17+0000",
              "uid": "allied-healthcare",
              "url": "https://yourcounterpart.com/products/allied-healthcare",
              "link_type": "Web",
              "key": "96f039cc-d0b1-4597-aeee-779c59830b78",
              "isBroken": false
            },
            "grandchild_3_name": "Architects, Engineers, & Contractors",
            "grandchild_3_link": {
              "id": "aVvpLRoAAEcA4tYA",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "architects-engineers--contractors-professional-liability",
              "first_publication_date": "2026-01-20T00:50:45+0000",
              "last_publication_date": "2026-01-21T00:10:34+0000",
              "uid": "architects-and-engineers",
              "url": "https://yourcounterpart.com/products/architects-and-engineers",
              "link_type": "Web",
              "key": "984a4142-97e9-4bd5-993a-da21d54f6751",
              "isBroken": false
            },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          }
        ],
        "primary": { "name": "Products", "logo_link": { "link_type": "Any" } },
        "id": "navigation_item_three_level$06e7716f-81d9-44e4-8067-f10b7e6b96bd",
        "slice_type": "navigation_item_three_level",
        "slice_label": null
      },
      {
        "variation": "default",
        "version": "initial",
        "items": [
          {
            "child_name": "Risk Mitigation",
            "child_link": {
              "id": "Z851gRoAAEgAb-hx",
              "type": "service",
              "tags": [],
              "lang": "en-us",
              "slug": "agentic-risk-mitigation",
              "first_publication_date": "2025-03-13T00:03:08+0000",
              "last_publication_date": "2026-04-28T23:23:23+0000",
              "uid": "risk-mitigation",
              "url": "https://yourcounterpart.com/services/risk-mitigation",
              "link_type": "Web",
              "key": "6daf2a31-2a83-423b-80f7-35b8d0ad9ded",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Claims Management",
            "child_link": {
              "id": "ZxHv9RoAAEEAvihJ",
              "type": "service",
              "tags": [],
              "lang": "en-us",
              "slug": "agentic-claims-management",
              "first_publication_date": "2024-11-22T00:19:29+0000",
              "last_publication_date": "2026-04-28T23:23:07+0000",
              "uid": "claims-management",
              "url": "https://yourcounterpart.com/services/claims-management",
              "link_type": "Web",
              "key": "a142c64f-fe0e-4ac6-9565-a77b9c9986e4",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          }
        ],
        "primary": {
          "name": "For Businesses",
          "logo_link": { "link_type": "Any" }
        },
        "id": "navigation_item_three_level$a51af52e-ec6a-4b44-93e0-d70f2a0e1fcc",
        "slice_type": "navigation_item_three_level",
        "slice_label": null
      },
      {
        "variation": "default",
        "version": "initial",
        "items": [
          {
            "child_name": "Services",
            "child_link": {
              "id": "ZM0X7BEAACAAblsN",
              "type": "broker_service",
              "tags": [],
              "lang": "en-us",
              "slug": "broker-service",
              "first_publication_date": "2023-08-04T15:23:28+0000",
              "last_publication_date": "2026-04-28T23:23:44+0000",
              "uid": "brokers",
              "url": "https://yourcounterpart.com/brokers",
              "link_type": "Web",
              "key": "b698b974-598e-4132-b3ab-b912bd17e865",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Our Appetite",
            "child_link": {
              "id": "aF1W-xkAAEsAuMod",
              "type": "product",
              "tags": [],
              "lang": "en-us",
              "slug": "brand-light",
              "first_publication_date": "2025-06-27T19:26:45+0000",
              "last_publication_date": "2026-04-28T23:24:43+0000",
              "uid": "our-appetite",
              "url": "https://yourcounterpart.com/products/our-appetite",
              "link_type": "Web",
              "key": "29c3f9d7-4e8c-44f5-ac00-9b8a017b8068",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Digital Application",
            "child_link": {
              "id": "Zz_Xth4AAEgAftbl",
              "type": "service",
              "tags": [],
              "lang": "en-us",
              "slug": "digital-application",
              "first_publication_date": "2025-02-05T19:00:11+0000",
              "last_publication_date": "2026-04-28T23:24:15+0000",
              "uid": "digital-application",
              "url": "https://yourcounterpart.com/services/digital-application",
              "link_type": "Web",
              "key": "b70fa6df-694c-439e-859e-48a673a222a1",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "API Partners",
            "child_link": {
              "id": "ZbvqlRIAADIG_vtE",
              "type": "service",
              "tags": [],
              "lang": "en-us",
              "slug": "api",
              "first_publication_date": "2024-02-01T19:07:11+0000",
              "last_publication_date": "2026-04-28T23:23:59+0000",
              "uid": "api",
              "url": "https://yourcounterpart.com/services/api",
              "link_type": "Web",
              "key": "dc034a15-c22a-4e96-92ca-fe3469167760",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Knowledge Center",
            "child_link": {
              "link_type": "Web",
              "key": "2dee286f-c48f-4db1-adc1-07599433e639",
              "url": "https://knowledge.yourcounterpart.com/en/"
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          }
        ],
        "primary": {
          "name": "For Brokers",
          "logo_link": { "link_type": "Any" }
        },
        "id": "navigation_item_three_level$7e200550-42e4-416c-a977-586f01fa8f05",
        "slice_type": "navigation_item_three_level",
        "slice_label": null
      },
      {
        "variation": "default",
        "version": "initial",
        "items": [
          {
            "child_name": "About Us",
            "child_link": {
              "id": "ZQhkMxAAACgAeeTO",
              "type": "about_us",
              "tags": [],
              "lang": "en-us",
              "slug": "about-us",
              "first_publication_date": "2023-09-18T14:52:37+0000",
              "last_publication_date": "2026-04-24T20:00:11+0000",
              "uid": "about",
              "url": "https://yourcounterpart.com/about",
              "link_type": "Web",
              "key": "f79646e0-9cec-4a3a-8244-cbbc9fa19ef5",
              "isBroken": false
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Careers",
            "child_link": {
              "link_type": "Web",
              "key": "73f6a2bc-699e-405b-9984-eb1f90412f94",
              "url": "https://yourcounterpart.com/about#careers",
              "target": "_blank"
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Press",
            "child_link": {
              "link_type": "Web",
              "key": "4e16880c-4fa1-4241-b3ba-2e9b38a9cc49",
              "url": "https://press.yourcounterpart.com"
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          },
          {
            "child_name": "Blog",
            "child_link": {
              "link_type": "Web",
              "key": "04fbc795-d320-4479-85b3-d44f7eb0b96a",
              "url": "https://blog.yourcounterpart.com"
            },
            "grandchild_1_name": null,
            "grandchild_1_link": { "link_type": "Any" },
            "grandchild_2_name": null,
            "grandchild_2_link": { "link_type": "Any" },
            "grandchild_3_name": null,
            "grandchild_3_link": { "link_type": "Any" },
            "grandchild_4_name": null,
            "grandchild_4_link": { "link_type": "Any" },
            "grandchild_5_name": null,
            "grandchild_5_link": { "link_type": "Any" },
            "grandchild_6_name": null,
            "grandchild_6_link": { "link_type": "Any" },
            "grandchild_7_name": null,
            "grandchild_7_link": { "link_type": "Any" },
            "grandchild_8_name": null,
            "grandchild_8_link": { "link_type": "Any" },
            "grandchild_9_name": null,
            "grandchild_9_link": { "link_type": "Any" },
            "grandchild_10_name": null,
            "grandchild_10_link": { "link_type": "Any" }
          }
        ],
        "primary": { "name": "Company", "logo_link": { "link_type": "Any" } },
        "id": "navigation_item_three_level$4e883153-44fe-4a5b-b512-18e88c46c32e",
        "slice_type": "navigation_item_three_level",
        "slice_label": null
      }
    ],
  }
}

/* ============================================================================
 * Desktop level-3 flyout
 * ========================================================================== */

const DesktopFlyout: React.FC<{
  parentId: string
  items: NavNode[]
  pathname: string | undefined
}> = ({ parentId, items, pathname }) => {
  return (
    <Flyout id={`${parentId}-flyout`} role="menu">
      <FlyoutList>
        {items.map((grandchild) => {
          const url = getLinkUrl(grandchild.link)
          const isActive = !!pathname && getPathname(url) === pathname
          return (
            <FlyoutItem key={grandchild.id} $active={isActive}>
              <PrismicLink type="noWrapper" link={grandchild.link}>
                <p>{grandchild.label}</p>
              </PrismicLink>
            </FlyoutItem>
          )
        })}
      </FlyoutList>
    </Flyout>
  )
}

/* ============================================================================
 * Desktop level-2 item (optionally contains a level-3 flyout)
 * ========================================================================== */

const DesktopSecondLevelItem: React.FC<{
  node: NavNode
  pathname: string | undefined
}> = ({ node, pathname }) => {
  const [hovered, setHovered] = useState(false)
  const hasChildren = !!node.children && node.children.length > 0
  const url = getLinkUrl(node.link)
  const exactMatch = !!pathname && getPathname(url) === pathname
  const descendantActive = hasChildren && isDescendantActive(node, pathname)
  const isActive = exactMatch || descendantActive

  const handleMouseEnter = () => setHovered(true)
  const handleMouseLeave = () => setHovered(false)
  const handleFocus = () => setHovered(true)
  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setHovered(false)
    }
  }

  return (
    <L2Item
      $active={isActive}
      $hasChildren={hasChildren}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      role="none"
    >
      {hasChildren ? (
        <>
          {getLinkUrl(node.link) ? (
            <PrismicLink
              type="noWrapper"
              link={node.link}
            >
              {node.label}
            </PrismicLink>
          ) : (
            <button
              type="button"
              aria-expanded={hovered}
              aria-haspopup="true"
              aria-controls={`${node.id}-flyout`}
              onClick={() => setHovered((h) => !h)}
            >
              {node.label}
            </button>
          )}
          {hovered && node.children && (
            <DesktopFlyout
              parentId={node.id}
              items={node.children}
              pathname={pathname}
            />
          )}
        </>
      ) : (
        <PrismicLink type="noWrapper" link={node.link}>
          {node.label}
        </PrismicLink>
      )}
    </L2Item>
  )
}

/* ============================================================================
 * Desktop level-1 dropdown (contains level-2 items with optional level-3)
 * ========================================================================== */

const DesktopThreeLevelDropdown: React.FC<{
  node: NavNode
  pathname: string | undefined
  color?: string
  isScrolled: boolean
}> = ({ node, pathname, color, isScrolled }) => {
  const [expanded, setExpanded] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  const closeDropdown = useCallback(
    (e: MouseEvent) => {
      if (expanded && bodyRef.current && !bodyRef.current.contains(e.target as Node)) {
        setExpanded(false)
      }
    },
    [expanded]
  )

  const handleMouseEnter = () => setExpanded(true)
  const handleMouseLeave = () => setExpanded(false)
  const handleFocus = () => setExpanded(true)
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!bodyRef.current?.contains(e.relatedTarget as Node)) {
      setExpanded(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', closeDropdown)
    return () => document.removeEventListener('click', closeDropdown)
  }, [closeDropdown])

  const link = node.link
  const linkUrl = getLinkUrl(link)
  const linkType = link?.link_type
  const hasLink = linkType && linkType !== 'Any'
  const children = node.children ?? []
  const hasChildren = children.length > 0
  const exactMatch = !!pathname && getPathname(linkUrl) === pathname
  const anyDescendantActive = isDescendantActive(node, pathname)
  const showActive = exactMatch || anyDescendantActive

  const dropdownId = `${node.id}-dropdown`

  return (
    <DropdownBody
      className={expanded ? 'expanded' : ''}
      $expanded={expanded}
      ref={bodyRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {hasLink ? (
        <CustomLinkWrapper
          className={exactMatch ? 'active' : 'noActive'}
          $showArrow={hasChildren}
        >
          <PrismicLink
            type="noWrapper"
            link={link}
          >
            <span className={`${!isScrolled ? getColor(color) : ''}`}>{node.label}</span>
          </PrismicLink>
        </CustomLinkWrapper>
      ) : (
        <DropdownHead
          type="button"
          className={`${showActive ? 'active' : 'noActive'} ${!isScrolled ? getColor(color) : ''}`}
          $withDropdown={hasChildren}
          aria-haspopup={hasChildren ? 'true' : undefined}
          aria-expanded={hasChildren ? expanded : undefined}
          aria-controls={hasChildren ? dropdownId : undefined}
          onClick={() => setExpanded((s) => !s)}
        >
          <p>{node.label}</p>
        </DropdownHead>
      )}

      {hasChildren && expanded && (
        <DropdownContent id={dropdownId} role="menu">
          <DropdownInnerContent>
            {children.map((child) => (
              <DesktopSecondLevelItem key={child.id} node={child} pathname={pathname} />
            ))}
          </DropdownInnerContent>
        </DropdownContent>
      )}
    </DropdownBody>
  )
}

/* ============================================================================
 * Mobile level-2 item (collapsible for level 3)
 * ========================================================================== */

const MobileNestedItem: React.FC<{
  node: NavNode
  pathname: string | undefined
}> = ({ node, pathname }) => {
  const hasChildren = !!node.children && node.children.length > 0
  const startExpanded = isDescendantActive(node, pathname)
  const [expanded, setExpanded] = useState(startExpanded)

  useEffect(() => {
    if (startExpanded) setExpanded(true)
  }, [startExpanded])

  const url = getLinkUrl(node.link)
  const exactMatch = !!pathname && getPathname(url) === pathname

  if (!hasChildren) {
    return (
      <li className={exactMatch ? 'underline' : ''}>
        <MobileItemLinkWrapper>
          <PrismicLink type="noWrapper" link={node.link}>
            {node.label}
          </PrismicLink>
        </MobileItemLinkWrapper>
      </li>
    )
  }

  const panelId = `${node.id}-mobile-panel`

  return (
    <MobileL2Wrapper $expanded={expanded}>
      <MobileL2Button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((s) => !s)}
      >
        <span className={exactMatch ? 'underline' : ''}>{node.label}</span>
        <Angle aria-hidden>
          <svg
            role="presentation"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.29289 8.79289C5.68342 8.40237 6.31658 8.40237 6.70711 8.79289L12 14.0858L17.2929 8.79289C17.6834 8.40237 18.3166 8.40237 18.7071 8.79289C19.0976 9.18342 19.0976 9.81658 18.7071 10.2071L12 16.9142L5.29289 10.2071C4.90237 9.81658 4.90237 9.18342 5.29289 8.79289Z"
              fill="currentColor"
            />
          </svg>
        </Angle>
      </MobileL2Button>

      <MobileLevel3List id={panelId} role="menu">
        {node.children!.map((grandchild) => {
          const gUrl = getLinkUrl(grandchild.link)
          const gActive = !!pathname && getPathname(gUrl) === pathname
          return (
            <li key={grandchild.id} className={gActive ? 'underline' : ''}>
              <PrismicLink type="noWrapper" link={grandchild.link}>
                {grandchild.label}
              </PrismicLink>
            </li>
          )
        })}
      </MobileLevel3List>
    </MobileL2Wrapper>
  )
}

/* ============================================================================
 * Mobile three-level nav section (a level-1 accordion)
 * ========================================================================== */

const MobileThreeLevelSection: React.FC<{
  node: NavNode
  pathname: string | undefined
  expanded: boolean
  onToggle: (id: string) => void
}> = ({ node, pathname, expanded, onToggle }) => {
  const children = node.children ?? []
  const hasChildren = children.length > 0
  const descendantActive = isDescendantActive(node, pathname)
  const linkType = node.link?.link_type
  const isClickToExpand = !linkType || linkType === 'Any'

  const panelId = `${node.id}-mobile-section`

  return (
    <Collapse $expanded={expanded} key={node.id}>
      {isClickToExpand ? (
        <HeaderButton
          name={node.id}
          aria-expanded={expanded}
          aria-controls={hasChildren ? panelId : undefined}
          onClick={() => onToggle(node.id)}
        >
          <P2 className={descendantActive ? 'underline' : ''}>{node.label}</P2>
          <Angle aria-hidden>
            <svg
              role="presentation"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.29289 8.79289C5.68342 8.40237 6.31658 8.40237 6.70711 8.79289L12 14.0858L17.2929 8.79289C17.6834 8.40237 18.3166 8.40237 18.7071 8.79289C19.0976 9.18342 19.0976 9.81658 18.7071 10.2071L12 16.9142L5.29289 10.2071C4.90237 9.81658 4.90237 9.18342 5.29289 8.79289Z"
                fill="#B0D3DD"
              />
            </svg>
          </Angle>
        </HeaderButton>
      ) : (
        <MobileLinkWrapper>
          <PrismicLink type="noWrapper" link={node.link}>{node.label}</PrismicLink>
        </MobileLinkWrapper>
      )}

      {hasChildren && (
        <CollapseBody id={panelId} role="menu">
          {children.map((child) => (
            <MobileNestedItem key={child.id} node={child} pathname={pathname} />
          ))}
        </CollapseBody>
      )}
    </Collapse>
  )
}

/* ============================================================================
 * Main component
 * ========================================================================== */

const Page = ({ params, transparent, heroBackgroundColor, buttonColor, color, logInVariant, getStartedVariant, location }: { params: any, transparent?: boolean, heroBackgroundColor?: string, buttonColor?: string, color?: any, logInVariant?: any, getStartedVariant?: any, location?: any }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowOffset, setWindowOffset] = useState(0);
  const isBrowser = typeof window !== 'undefined';
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedSectionId, setExpandedSectionId] = useState('');

  const pathname = location?.pathname

  const navTree = useMemo<NavNode[]>(
    () => normalizeNavigationData(globalParams.data.slices),
    [],
  )

  useEffect(() => {
    const activeTop = navTree.find((n) => isDescendantActive(n, pathname))
    if (activeTop) setExpandedSectionId(activeTop.id)
  }, [navTree, pathname])

  const handleSectionToggle = useCallback((id: string) => {
    setExpandedSectionId((current) => (current === id ? '' : id))
  }, [])

  const queryData = useStaticQuery(graphql`
      {
        prismicNavigation {
          data {
            scrolled_logo_image {
              url
            }
            unscrolled_logo_image {
              url
            }
            always_black_header {
              link {
                url
              }
            }
            log_in_link {
              url
              link_type
              target
            }
            get_started_link {
              url
              link_type
            }
            twitter_link {
              url
              link_type
              target
            }
            linkedin_page {
              url
              link_type
              target
            }
            body {
              ... on PrismicNavigationDataBodyNavigationDropdown {
                id
                items {
                  link_title {
                    text
                  }
                  linked_page {
                    url
                    link_type
                  }
                }
                primary {
                  dropdown_title {
                    text
                  }
                  linked_page {
                    url
                    link_type
                  }
                }
              }
            }
          }
        }
      }
    `)

  const navigation = queryData.prismicNavigation

  useEffect(() => {
    const handleScroll = throttle(function () {
      if (window.pageYOffset === 0 || window.pageYOffset > 0) {
        setWindowOffset(window.pageYOffset)
      }
    }, 300)

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleScroll)
    }

    handleScroll();

    return () => {
      window.removeEventListener('resize', handleScroll)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (isBrowser && windowOffset > 0 && !isScrolled) {
      setIsScrolled(true)
    } else if (isBrowser && windowOffset === 0 && isScrolled) {
      setIsScrolled(false)
    }
  }, [windowOffset, isBrowser, isScrolled])

  const handleMobileMenuClick = (e: any) => {
    e.preventDefault()
    setShowMobileMenu(s => !s)
  }

  return (
    <>
      {showMobileMenu && <Helmet>
        <style>
          {`body {
            overflow: hidden;
          }`}
        </style>
      </Helmet>}
      <StyledWrapper
        className=""
        $scrolled={isScrolled}
      >
        <StyledContainer>
          <HeaderComponent className="">
            <LogoWrapper className="">
              <PrismicLink className="" link="https://yourcounterpart.com">
                <img
                  src={navigation.data.scrolled_logo_image.url}
                  alt="Counterpart logo"
                  className="logo"
                />
              </PrismicLink>
            </LogoWrapper>
            <Nav>
              <DesktopMenu className="">
                {navTree.map((node) => (
                  <li className="" key={node.id}>
                    <DesktopThreeLevelDropdown
                      node={node}
                      pathname={pathname}
                      color={color}
                      isScrolled={isScrolled}
                    />
                  </li>
                ))}
              </DesktopMenu>
            </Nav>
            <LogIn $isScrolled={isScrolled}>
              <li>
                <CustomLink type="noWrapper" link="https://app.yourcounterpart.com" className="underlined" target={navigation.data.log_in_link.target}>
                  <P2>Log in</P2>
                  {(navigation.data.log_in_link.target === '_blank') && <span className="hidden">(link opens in new tab)</span>}
                </CustomLink>
              </li>
            </LogIn>
            <BurgerButton
              onClick={handleMobileMenuClick}
              aria-label="Mobile menu button"
              aria-expanded={showMobileMenu}
              aria-controls="mobile-nav-overlay"
              className={getColor(color)}
            />
            <MobileOverlay $showMobileMenu={showMobileMenu ? "true" : ""}>
              <MobileInner>
                <CloseButtonWrapper>
                  <LogoWrapper className="mobile">
                    <PrismicLink className="" link="https://yourcounterpart.com">
                      <img
                        src={LogoIcon}
                        alt="Counterpart logo"
                      />
                    </PrismicLink>
                  </LogoWrapper>
                  <CloseButton onClick={handleMobileMenuClick} aria-label="Close menu" />
                </CloseButtonWrapper>
                <MobileMenuWrapper>
                  {navTree.map((node) => (
                    <MobileThreeLevelSection
                      key={node.id}
                      node={node}
                      pathname={pathname}
                      expanded={expandedSectionId === node.id}
                      onToggle={handleSectionToggle}
                    />
                  ))}
                </MobileMenuWrapper>
                <MobileMenuButtonsWrapper>
                  <CustomLink type="noWrapper" link="https://app.yourcounterpart.com" className="underlined" target="_blank">
                    Log in
                    <span className="hidden">(link opens in new tab)</span>
                  </CustomLink>
                </MobileMenuButtonsWrapper>
                <MobileSocialWrapper>
                  <div>
                    <PrismicLink aria-label="LinkedIn social media link" link={globalParams.data.linkedin_icon_link}>
                      <svg role="presentation" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="16" fill="#F5F5AB" />
                        <path d="M18.666 13.3335C19.7269 13.3335 20.7443 13.7549 21.4944 14.5051C22.2446 15.2552 22.666 16.2726 22.666 17.3335V22.0002H19.9993V17.3335C19.9993 16.9799 19.8589 16.6407 19.6088 16.3907C19.3588 16.1406 19.0196 16.0002 18.666 16.0002C18.3124 16.0002 17.9733 16.1406 17.7232 16.3907C17.4732 16.6407 17.3327 16.9799 17.3327 17.3335V22.0002H14.666V17.3335C14.666 16.2726 15.0874 15.2552 15.8376 14.5051C16.5877 13.7549 17.6051 13.3335 18.666 13.3335Z" fill="#58583E" />
                        <path d="M12.0007 14H9.33398V22H12.0007V14Z" fill="#58583E" />
                        <path d="M10.6673 10.6667C11.4037 10.6667 12.0007 10.0697 12.0007 9.33333C12.0007 8.59695 11.4037 8 10.6673 8C9.93094 8 9.33398 8.59695 9.33398 9.33333C9.33398 10.0697 9.93094 10.6667 10.6673 10.6667Z" fill="#58583E" />
                      </svg>
                    </PrismicLink>
                  </div>
                  <div>
                    <PrismicLink aria-label="Twitter social media link" link={globalParams.data.twitter_icon_link}>
                      <svg role="presentation" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="16" fill="#F5F5AB" />
                        <path d="M25 10.7755C24.3381 11.077 23.6263 11.2804 22.8789 11.3717C23.6416 10.9033 24.227 10.1613 24.5028 9.27662C23.7892 9.71074 22.9992 10.0259 22.1577 10.1959C21.4843 9.45994 20.5244 9 19.4623 9C17.4229 9 15.7694 10.6955 15.7694 12.7867C15.7694 13.0835 15.8021 13.3723 15.865 13.6495C12.7958 13.4918 10.0745 11.9844 8.25316 9.69317C7.93539 10.2522 7.75324 10.9026 7.75324 11.5969C7.75324 12.9106 8.40516 14.0695 9.39605 14.7488C8.79086 14.7289 8.22119 14.5587 7.72343 14.275C7.7231 14.2907 7.7231 14.3066 7.7231 14.3229C7.7231 16.1573 8.99606 17.6877 10.6856 18.0359C10.3757 18.1222 10.0494 18.1687 9.71255 18.1687C9.47435 18.1687 9.24314 18.1451 9.01762 18.1007C9.48776 19.605 10.8515 20.6999 12.4672 20.7304C11.2034 21.7462 9.61119 22.3515 7.8808 22.3515C7.58286 22.3515 7.28902 22.3334 7 22.2986C8.63459 23.3732 10.5755 24 12.6609 24C19.4537 24 23.1679 18.2298 23.1679 13.2258C23.1679 13.0619 23.1647 12.8981 23.1576 12.7359C23.8789 12.202 24.5053 11.5351 25 10.7755Z" fill="#58583E" />
                      </svg>
                    </PrismicLink>
                  </div>
                </MobileSocialWrapper>
              </MobileInner>
            </MobileOverlay>
          </HeaderComponent>
        </StyledContainer>
      </StyledWrapper>
    </>
  );
};

export default Page;
